<template>
  <model-form
    ref="modelForm"
    :model="timeClockSession"
    modal-type="modal"
  >
    <div>
      <div class="row q-col-gutter-md q-mb-md">
        <q-input
          v-if="timeClockSession.isRemote"
          :model-value="timeClockSession?.user?.name || ''"
          class="col-6"
          label="User"
          readonly
        />
        <model-form-select
          v-else
          field="user"
          class="col-6"
          label="User"
          use-input
          input-debounce="300"
          input-style="search"
          :options="users.data"
          option-label="name"
          map-options
          clearable
          @filter="userFilterFunction"
        />
        <q-select
          v-if="$admin && currentUser.admin"
          v-model="timeClockSession.state"
          class="col-6"
          label="State"
          map-options
          emit-value
          :disable="!canEditState"
          :options="$mh.asOptions(TIME_CLOCK_SESSION_STATES.ALL)"
        />
        <q-select
          v-model="timeClockSession.employeeJobTypeId"
          class="col-6"
          label="Job Type"
          :options="employeeJobTypes.data"
          option-label="name"
          option-value="id"
          map-options
          emit-value
        />
        <q-input
          v-model.number="timeClockSession.hourlyRate"
          class="col-6"
          label="Hourly Rate"
          type="number"
          step="0.01"
          :prefix="currentCircuit.localeCurrencySymbol"
        />
        <q-input
          :model-value="timeClockSession.startedAt ? IsoDateTime.parse(timeClockSession.startedAt).i18n('shortDate') : null"
          class="col-6"
          label="Start Date"
        >
          <template #append>
            <q-icon
              name="event"
              class="cursor-pointer"
            >
              <q-popup-proxy
                transition-show="scale"
                transition-hide="scale"
              >
                <q-date
                  :model-value="formatDate(timeClockSession.startedAt, 'YYYY-MM-DD')"
                  mask="YYYY-MM-DD"
                  @update:model-value="setStartDate"
                >
                  <div class="row items-center justify-end">
                    <q-btn
                      v-close-popup
                      label="Close"
                      color="primary"
                      flat
                    />
                  </div>
                </q-date>
              </q-popup-proxy>
            </q-icon>
          </template>
        </q-input>
        <time-setter
          :time="timeClockSession.startedAt"
          label="Start Time"
          class="col-6"
          @update-time="updateStartTime"
        />
        <q-input
          :model-value="timeClockSession.endedAt ? IsoDateTime.parse(timeClockSession.endedAt).i18n('shortDate') : null"
          class="col-6"
          label="End Date"
        >
          <template #append>
            <q-icon
              name="event"
              class="cursor-pointer"
            >
              <q-popup-proxy
                transition-show="scale"
                transition-hide="scale"
              >
                <q-date
                  :model-value="formatDate(timeClockSession.endedAt, 'YYYY-MM-DD')"
                  mask="YYYY-MM-DD"
                  @update:model-value="setEndDate"
                >
                  <div class="row items-center justify-end">
                    <q-btn
                      v-close-popup
                      label="Close"
                      color="primary"
                      flat
                    />
                  </div>
                </q-date>
              </q-popup-proxy>
            </q-icon>
          </template>
        </q-input>
        <time-setter
          :time="timeClockSession.endedAt"
          class="col-6"
          hint="Times are all in site's timezone."
          label="End Time"
          @update-time="updateEndTime"
        />
        <div class="col-12">
          <q-btn
            v-if="timeClockSession.isRemote && !timeClockSession.endedAt"
            class="full-width"
            icon="stop"
            color="negative"
            size="lg"
            @click="clockOff"
          >
            Clock Off
          </q-btn>
          <div />
        </div>
      </div>
    </div>
  </model-form>
</template>

<script>
import { date } from 'quasar'
import { IsoDateTime } from 'src/models/model'
import { TIME_CLOCK_SESSION_STATES } from 'src/models/time-clock/TimeClockSession'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import TimeSetter from 'src/modules/admin/components/TimeSetter'

const { formatDate } = date

export default {
  name: 'TimeClockSessionForm',
  components: {
    ModelForm,
    TimeSetter
  },
  models: {
    timeClockSession: {
      user: {
        fields: ['id', 'firstName', 'lastName'],
        userEmployeeJobTypes: {
          fields: ['id', 'overrideHourlyRate'],
          employeeJobType: {
            fields: ['id', 'name', 'hourlyRate']
          }
        }
      },
      employeeJobType: 'default'
    },
    users: {
      fields: ['id', 'firstName', 'lastName'],
      userEmployeeJobTypes: {
        fields: ['id', 'overrideHourlyRate'],
        employeeJobType: {
          fields: ['id', 'name', 'hourlyRate']
        }
      },
      variables () {
        return {
          userRole: 'employees',
          limit: 100,
          orderBy: 'firstName',
          searchString: this.userSearchValue
        }
      }
    }
  },
  configuration: ['currentCircuit', 'employeeJobTypes'],
  context: ['currentUser'],
  data () {
    return {
      userSearchValue: ''
    }
  },
  computed: {
    canEditState () {
      return !_.isNil(this.timeClockSession.endedAt)
    }
  },
  watch: {
    'timeClockSession.employeeJobType': {
      handler (newVal, oldVal) {
        if (newVal && (!oldVal || newVal.id !== oldVal?.id)) {
          this.setHourlyRate()
        }
      }
    },
    'timeClockSession.user': {
      handler (newVal, oldVal) {
        if (newVal && (!oldVal || newVal.id !== oldVal?.id)) {
          this.setHourlyRate()
        }
      }
    }
  },
  created () {
    _.extend(this, { IsoDateTime, formatDate, TIME_CLOCK_SESSION_STATES })
  },
  methods: {
    setStartDate (value) {
      const values = value.split('-')
      const year = values[0]
      const month = values[1] - 1 // Month is 0 indexed
      const date = values[2]

      let startDate
      if (this.timeClockSession.startedAt) {
        startDate = new Date(this.timeClockSession.startedAt)
      } else {
        startDate = new Date()
      }

      startDate.setYear(year)
      startDate.setMonth(month)
      startDate.setDate(date)
      startDate.setSeconds(0)
      startDate.setMilliseconds(0)
      this.timeClockSession.startedAt = startDate
    },
    setEndDate (value) {
      const values = value.split('-')
      const year = values[0]
      const month = values[1] - 1 // Month is 0 indexed
      const date = values[2]

      let endDate
      if (this.timeClockSession.endedAt) {
        endDate = new Date(this.timeClockSession.endedAt)
      } else {
        endDate = new Date()
      }
      endDate.setYear(year)
      endDate.setMonth(month)
      endDate.setDate(date)
      endDate.setSeconds(0)
      endDate.setMilliseconds(0)
      this.timeClockSession.endedAt = endDate
    },
    async clockOff () {
      await this.timeClockSession.stop()
    },
    userFilterFunction (val, update, abort) {
      update(() => {
        this.userSearchValue = val
      })
    },
    setHourlyRate () {
      if (_.isNil(this.timeClockSession.employeeJobType)) return
      if (_.isNil(this.timeClockSession.user)) return

      const userEmployeeJobType = this.timeClockSession?.user?.hasJobType(this.timeClockSession.employeeJobType)
      if (userEmployeeJobType) {
        this.timeClockSession.hourlyRate = this.timeClockSession.user.userEmployeeJobTypeByJobType(this.timeClockSession.employeeJobType).hourlyRate
      } else {
        this.timeClockSession.hourlyRate = this.timeClockSession.employeeJobType.hourlyRate
      }
    },
    updateStartTime (newTime) {
      const time = new Date(newTime)
      time.setSeconds(0)
      time.setMilliseconds(0)
      this.timeClockSession.startedAt = time
    },
    updateEndTime (newTime) {
      const time = new Date(newTime)
      time.setSeconds(0)
      time.setMilliseconds(0)
      this.timeClockSession.endedAt = time
    }
  }
}

</script>
